import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

export const TeamDetailsPage = ({ userRole, pageProps, navigate }) => {
    const { teamId } = pageProps;
    const [teamDetails, setTeamDetails] = useState(null);
    const [roster, setRoster] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [matchContext, setMatchContext] = useState({ nextMatch: null, lastMatch: null });
    const [teamRank, setTeamRank] = useState(null);
    const [teamRanksMap, setTeamRanksMap] = useState({});
    const [teamStats, setTeamStats] = useState(null);
    const [rosterStats, setRosterStats] = useState([]);
    const [homeVenue, setHomeVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [selectedTeamForHistory, setSelectedTeamForHistory] = useState(null);
    const [selectedPlayerForHistory, setSelectedPlayerForHistory] = useState(null);
    const [allPlayers, setAllPlayers] = useState([]);
    const [allSchedules, setAllSchedules] = useState([]);
    const [allPlayersStats, setAllPlayersStats] = useState({});

    useEffect(() => {
        let isMounted = true;
        const fetchTeamData = async () => {
            if (!teamId) {
                if (isMounted) setLoading(false);
                return;
            }
            if (isMounted) setLoading(true);
            try {
                // Teams for lookups
                const teamsSnapshot = await getDocs(collection(db, "teams"));
                const teamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (isMounted) setAllTeams(teamsData);

                const usersSnapshot = await getDocs(collection(db, "users"));
                const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (isMounted) setAllPlayers(usersData);

                // Team doc
                const teamDoc = await getDoc(doc(db, "teams", teamId));
                if (teamDoc.exists()) {
                    const teamData = { id: teamDoc.id, ...teamDoc.data() };
                    if (isMounted) setTeamDetails(teamData);

                    // Venue
                    if (teamData.homeLocation) {
                        const venueQ = query(collection(db, "venues"), where("name", "==", teamData.homeLocation));
                        const venueSnap = await getDocs(venueQ);
                        if (!venueSnap.empty && isMounted) setHomeVenue(venueSnap.docs[0].data());
                    }

                    // Roster
                    let playerIds = [];
                    if (teamData.archived) {
                        // For archived teams, players are in `formerPlayers` or `members` (legacy)
                        const formerPlayerIds = (teamData.formerPlayers || []).map(p => p.id);
                        playerIds = [...new Set([...(teamData.members || []), ...formerPlayerIds])];
                    } else {
                        playerIds = teamData.members || [];
                    }
                    const rosterData = playerIds.map(memberId => usersData.find(u => u.id === memberId)).filter(Boolean);
                    if (isMounted) setRoster(rosterData);

                    // Rank (minimal standings)
                    const schedulesSnap = await getDocs(collection(db, "schedules"));
                    const schedules = schedulesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    if (isMounted) setAllSchedules(schedules);
                    const currentSchedule = schedules.filter(s => !s.archived)[0] || null;
                    let finalTeamStats = null;

                    if (currentSchedule && Array.isArray(currentSchedule.weeks)) {
                        const standingsMap = {};
                        teamsData.forEach(t => { standingsMap[t.id] = { gamePoints: 0, matchWins: 0 }; });
                        currentSchedule.weeks.forEach(week => {
                            if (!week || !Array.isArray(week.games)) return;
                            week.games.forEach(match => {
                                const res = match?.results;
                                if (!res) return;
                                const homePoints = res?.matchStats?.home?.gamePoints ?? 0;
                                const awayPoints = res?.matchStats?.away?.gamePoints ?? 0;
                                if (standingsMap[match.homeTeamId]) {
                                    standingsMap[match.homeTeamId].gamePoints += homePoints;
                                    if (homePoints > awayPoints) standingsMap[match.homeTeamId].matchWins++;
                                }
                                if (standingsMap[match.awayTeamId]) {
                                    standingsMap[match.awayTeamId].gamePoints += awayPoints;
                                    if (awayPoints > homePoints) standingsMap[match.awayTeamId].matchWins++;
                                }
                            });
                        });
                        const sorted = Object.keys(standingsMap).sort((a, b) => {
                            const teamA_stats = standingsMap[a];
                            const teamB_stats = standingsMap[b];

                            if (teamB_stats.gamePoints !== teamA_stats.gamePoints) {
                                return teamB_stats.gamePoints - teamA_stats.gamePoints;
                            }

                            // Head-to-head tiebreaker
                            if (currentSchedule && currentSchedule.weeks) {
                                for (const week of currentSchedule.weeks) {
                                    for (const game of week.games) {
                                        const isMatch = (game.homeTeamId === a && game.awayTeamId === b) || (game.homeTeamId === b && game.awayTeamId === a);
                                        if (isMatch && game.results && game.results.matchStats) {
                                            const homePoints = game.results.matchStats.home.gamePoints;
                                            const awayPoints = game.results.matchStats.away.gamePoints;
                                            if (homePoints !== awayPoints) {
                                                const winnerId = homePoints > awayPoints ? game.homeTeamId : game.awayTeamId;
                                                if (winnerId === a) return -1; // a wins, ranked higher
                                                if (winnerId === b) return 1;  // b wins, ranked higher
                                            }
                                        }
                                    }
                                }
                            }
                            return teamB_stats.matchWins - teamA_stats.matchWins;
                        });
                        const idx = sorted.findIndex(id => id === teamId);
                        if (isMounted) setTeamRank(idx >= 0 ? idx + 1 : null);
                        const ranks = {};
                        sorted.forEach((id, i) => { ranks[id] = i + 1; });
                        if (isMounted) setTeamRanksMap(ranks);

                        // --- Compute Team Stats & Player Stats for this team ---
                        const initType = () => ({ w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 });
                        const teamStatsData = {};
                        teamsData.forEach(t => {
                            teamStatsData[t.id] = {
                                id: t.id,
                                name: t.name,
                                gamePoints: 0, matchWins: 0, matchLosses: 0, weeksPlayed: new Set(), gameHistory: [], matchHistory: [],
                                S301: initType(), D301: initType(),
                                S501: initType(), D501: initType(),
                                SCricket: initType(), DCricket: initType(),
                            };
                        });
                        const playerStatsData = {};
                        usersData.forEach(player => {
                            playerStatsData[player.id] = {
                                id: player.id,
                                name: player.nickname || `${player.firstName} ${player.lastName}`,
                                totalGamesPlayed: 0, totalGamesWon: 0, weeksPlayed: new Set(),
                                totalLegsPlayed: 0, totalLegsWon: 0, gameHistory: [],
                                S301: initType(), D301: initType(),
                                S501: initType(), D501: initType(),
                                SCricket: initType(), DCricket: initType(),
                            };
                        });

                        currentSchedule.weeks.forEach((week, wIdx) => {
                            if (!week || !Array.isArray(week.games)) return;
                            const weekIdentifier = `${currentSchedule.id}-${week.week ?? wIdx}`;
                            week.games.forEach((match, gIdx) => {
                                const res = match?.results;
                                const homeId = match.homeTeamId;
                                const awayId = match.awayTeamId;

                                const getTeamMatchSnapshot = (tid) => {
                                    if (!teamStatsData[tid]) return null;
                                    const s = teamStatsData[tid];
                                    const totalMatches = s.matchWins + s.matchLosses;
                                    return { w: s.matchWins, l: s.matchLosses, winPct: totalMatches > 0 ? s.matchWins / totalMatches : 0 };
                                };
                                const getTeamGameSnapshot = (tid, type) => {
                                    if (!teamStatsData[tid]) return null;
                                    const s = teamStatsData[tid][type];
                                    return { w: s.w, l: s.l, winPct: s.legsPlayed > 0 ? s.legsWon / s.legsPlayed : 0 };
                                };
                                const getPlayerGameSnapshot = (pid, type) => {
                                    if (!playerStatsData[pid]) return null;
                                    const s = playerStatsData[pid][type];
                                    return { w: s.w, l: s.l, legsWon: s.legsWon, legsPlayed: s.legsPlayed, winPct: s.legsPlayed > 0 ? s.legsWon / s.legsPlayed : 0 };
                                };

                                const homeMatchRecordBefore = getTeamMatchSnapshot(homeId);
                                const awayMatchRecordBefore = getTeamMatchSnapshot(awayId);

                                if (Array.isArray(res?.gameScores)) {
                                    res.gameScores.forEach((score, idx) => {
                                        const gameType = gameStructure[idx]?.type;
                                        if (!gameType) return;
                                        const isSingles = gameType.startsWith('S');
                                        const homePlayers = isSingles ? [res.playerAssignments?.[`${idx}-home-p1`]] : [res.playerAssignments?.[`${idx}-home-p1`], res.playerAssignments?.[`${idx}-home-p2`]];
                                        const awayPlayers = isSingles ? [res.playerAssignments?.[`${idx}-away-p1`]] : [res.playerAssignments?.[`${idx}-away-p1`], res.playerAssignments?.[`${idx}-away-p2`]];
                                        
                                        const homeWonGame = score.home > score.away;
                                        const totalLegs = score.home + score.away;

                                        const homeTeamGameRecordBefore = getTeamGameSnapshot(homeId, gameType);
                                        const awayTeamGameRecordBefore = getTeamGameSnapshot(awayId, gameType);

                                        const homePlayerRecordsBefore = {};
                                        homePlayers.filter(Boolean).forEach(pid => homePlayerRecordsBefore[pid] = getPlayerGameSnapshot(pid, gameType));
                                        const awayPlayerRecordsBefore = {};
                                        awayPlayers.filter(Boolean).forEach(pid => awayPlayerRecordsBefore[pid] = getPlayerGameSnapshot(pid, gameType));

                                        homePlayers.filter(Boolean).forEach(pid => {
                                            if (!playerStatsData[pid]) return;
                                            playerStatsData[pid].totalGamesPlayed++;
                                            playerStatsData[pid].weeksPlayed.add(weekIdentifier);
                                            playerStatsData[pid].totalLegsPlayed += totalLegs;
                                            playerStatsData[pid].totalLegsWon += score.home;
                                            if (homeWonGame) playerStatsData[pid].totalGamesWon++;
                                        });

                                        awayPlayers.filter(Boolean).forEach(pid => {
                                            if (!playerStatsData[pid]) return;
                                            playerStatsData[pid].totalGamesPlayed++;
                                            playerStatsData[pid].weeksPlayed.add(weekIdentifier);
                                            playerStatsData[pid].totalLegsPlayed += totalLegs;
                                            playerStatsData[pid].totalLegsWon += score.away;
                                            if (!homeWonGame) playerStatsData[pid].totalGamesWon++;
                                        });

                                        if (teamStatsData[homeId]) {
                                            teamStatsData[homeId][gameType].legsWon += score.home;
                                            teamStatsData[homeId][gameType].legsPlayed += totalLegs;
                                            if (homeWonGame) teamStatsData[homeId][gameType].w++; else teamStatsData[homeId][gameType].l++;
                                        }
                                        if (teamStatsData[awayId]) {
                                            teamStatsData[awayId][gameType].legsWon += score.away;
                                            teamStatsData[awayId][gameType].legsPlayed += totalLegs;
                                            if (!homeWonGame) teamStatsData[awayId][gameType].w++; else teamStatsData[awayId][gameType].l++;
                                        }

                                        // Apply Historical Records for Modal
                                        if (teamStatsData[homeId]) {
                                            teamStatsData[homeId].gameHistory.push({
                                                week: week.week, date: week.date, gameName: gameStructure[idx].name, gameType,
                                                teamLegs: score.home, opponentLegs: score.away, won: homeWonGame, opponentId: awayId,
                                                isSingles, teamPlayerIds: homePlayers.filter(Boolean), opponentPlayerIds: awayPlayers.filter(Boolean),
                                                teamRecordAtTime: homeTeamGameRecordBefore || { w: 0, l: 0, winPct: 0 },
                                                opponentRecordAtTime: awayTeamGameRecordBefore || { w: 0, l: 0, winPct: 0 }
                                            });
                                        }
                                        if (teamStatsData[awayId]) {
                                            teamStatsData[awayId].gameHistory.push({
                                                week: week.week, date: week.date, gameName: gameStructure[idx].name, gameType,
                                                teamLegs: score.away, opponentLegs: score.home, won: !homeWonGame, opponentId: homeId,
                                                isSingles, teamPlayerIds: awayPlayers.filter(Boolean), opponentPlayerIds: homePlayers.filter(Boolean),
                                                teamRecordAtTime: awayTeamGameRecordBefore || { w: 0, l: 0, winPct: 0 },
                                                opponentRecordAtTime: homeTeamGameRecordBefore || { w: 0, l: 0, winPct: 0 }
                                            });
                                        }

                                        homePlayers.filter(Boolean).forEach(pid => {
                                            if(playerStatsData[pid]) {
                                                playerStatsData[pid].gameHistory.push({
                                                    week: week.week, date: week.date, gameName: gameStructure[idx].name, gameType,
                                                    isSingles, playerLegs: score.home, opponentLegs: score.away, won: homeWonGame,
                                                    opponentIds: awayPlayers.filter(Boolean),
                                                    playerRecordAtTime: homePlayerRecordsBefore[pid] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                                                    opponentRecordsAtTime: awayPlayers.filter(Boolean).map(oppId => awayPlayerRecordsBefore[oppId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 })
                                                });
                                            }
                                        });
                                        awayPlayers.filter(Boolean).forEach(pid => {
                                            if(playerStatsData[pid]) {
                                                playerStatsData[pid].gameHistory.push({
                                                    week: week.week, date: week.date, gameName: gameStructure[idx].name, gameType,
                                                    isSingles, playerLegs: score.away, opponentLegs: score.home, won: !homeWonGame,
                                                    opponentIds: homePlayers.filter(Boolean),
                                                    playerRecordAtTime: awayPlayerRecordsBefore[pid] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                                                    opponentRecordsAtTime: homePlayers.filter(Boolean).map(oppId => homePlayerRecordsBefore[oppId] || { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 })
                                                });
                                            }
                                        });
                                    });
                                }

                                if (res) {
                                    const homePoints = res?.matchStats?.home?.gamePoints ?? 0;
                                    const awayPoints = res?.matchStats?.away?.gamePoints ?? 0;
                                    if (teamStatsData[homeId]) {
                                        teamStatsData[homeId].weeksPlayed.add(weekIdentifier);
                                        teamStatsData[homeId].gamePoints += homePoints;
                                    }
                                    if (teamStatsData[awayId]) {
                                        teamStatsData[awayId].weeksPlayed.add(weekIdentifier);
                                        teamStatsData[awayId].gamePoints += awayPoints;
                                    }
                                    if (homePoints > awayPoints) {
                                        if (teamStatsData[homeId]) teamStatsData[homeId].matchWins++;
                                        if (teamStatsData[awayId]) teamStatsData[awayId].matchLosses++;
                                    } else if (awayPoints > homePoints) {
                                        if (teamStatsData[awayId]) teamStatsData[awayId].matchWins++;
                                        if (teamStatsData[homeId]) teamStatsData[homeId].matchLosses++;
                                    }

                                    // Update Team Match History
                                    if (teamStatsData[homeId]) {
                                        teamStatsData[homeId].matchHistory.push({
                                            week: week.week, date: week.date, teamPoints: homePoints, opponentPoints: awayPoints,
                                            won: homePoints > awayPoints, opponentId: awayId,
                                            teamRecordAtTime: homeMatchRecordBefore || { w: 0, l: 0, winPct: 0 },
                                            opponentRecordAtTime: awayMatchRecordBefore || { w: 0, l: 0, winPct: 0 }
                                        });
                                    }
                                    if (teamStatsData[awayId]) {
                                        teamStatsData[awayId].matchHistory.push({
                                            week: week.week, date: week.date, teamPoints: awayPoints, opponentPoints: homePoints,
                                            won: awayPoints > homePoints, opponentId: homeId,
                                            teamRecordAtTime: awayMatchRecordBefore || { w: 0, l: 0, winPct: 0 },
                                            opponentRecordAtTime: homeMatchRecordBefore || { w: 0, l: 0, winPct: 0 }
                                        });
                                    }
                                }
                            });
                        });

                        // Finalize Team Stats
                        const gameTypes = ['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'];
                        const allTeamStatsArray = Object.values(teamStatsData);
                        allTeamStatsArray.forEach(t => {
                            t.weeksPlayed = t.weeksPlayed.size;
                            gameTypes.forEach(key => {
                                if (t[key]) {
                                    const totalLegs = t[key].legsPlayed;
                                    t[key].winPct = totalLegs > 0 ? (t[key].legsWon / totalLegs) : 0;
                                }
                            });
                        });

                        const gameTypeRanks = {};
                        gameTypes.forEach(type => {
                            const sortedForType = [...allTeamStatsArray].sort((a, b) => {
                                if (b[type].winPct !== a[type].winPct) return b[type].winPct - a[type].winPct;
                                return b[type].w - a[type].w;
                            });
                            gameTypeRanks[type] = sortedForType.findIndex(t => t.id === teamId) + 1;
                        });

                        finalTeamStats = teamStatsData[teamId];
                        if (finalTeamStats) {
                            finalTeamStats.ranks = gameTypeRanks;
                            if (isMounted) setTeamStats(finalTeamStats);
                        }

                        // Finalize Roster Stats with Global Rank
                        const allPlayerStatsReady = {};
                        const allPlayerStatsArray = [];
                        Object.values(playerStatsData).forEach(p => { 
                            p.weeksPlayed = typeof p.weeksPlayed === 'object' ? p.weeksPlayed.size : p.weeksPlayed; 
                            allPlayerStatsReady[p.id] = p;
                            allPlayerStatsArray.push(p);
                        });
                        if (isMounted) setAllPlayersStats(allPlayerStatsReady);
                        allPlayerStatsArray.sort((a, b) => b.totalGamesWon - a.totalGamesWon);
                        const playerRanks = {};
                        allPlayerStatsArray.forEach((p, idx) => {
                            playerRanks[p.id] = idx + 1;
                        });

                        const finalRosterStats = rosterData.map(p => {
                            const st = playerStatsData[p.id];
                            if (st) st.overallRank = playerRanks[p.id];
                            return st;
                        }).filter(Boolean);

                        if (isMounted) setRosterStats(finalRosterStats);

                        // --- Compute Next & Last Match for this team ---
                        const allMyGames = currentSchedule.weeks.flatMap((week, wIdx) => (
                            (Array.isArray(week.games) ? week.games : [])
                                .filter(g => (g.homeTeamId === teamId || g.awayTeamId === teamId) && !g.eventName)
                                .map((g, gi) => ({
                                    ...g,
                                    date: week.date,
                                    scheduleId: currentSchedule.id,
                                    weekIndex: wIdx,
                                    gameIndex: gi,
                                }))
                        )).sort((a, b) => new Date(a.date) - new Date(b.date));

                        const completedGames = allMyGames.filter(g => g.results || g.result);
                        const lastMatch = completedGames.length ? completedGames[completedGames.length - 1] : null;
                        const nextMatch = allMyGames.find(g => !(g.results || g.result));
                        if (isMounted) setMatchContext({ lastMatch, nextMatch });

                    }

                    // --- Team History (Cumulative Across All Seasons; archived is primary source) ---
                    const myTeamId = teamId;
                    const currentTeamName = teamData?.name || '';
                    const teamHistoryAgg = {
                        totalMatchWins: 0,
                        totalMatchLosses: 0,
                        gameWinsCount: { S301: 0, D301: 0, S501: 0, D501: 0, SCricket: 0, DCricket: 0 },
                        gameTotals: { S301: 0, D301: 0, S501: 0, D501: 0, SCricket: 0, DCricket: 0 },
                        formerPlayerIds: new Set(),
                        highlights: { tonPlus: 0, oneEighties: 0, highOut: 0, highOn: 0 },
                    };

                    const accumulateForSchedule = (schedule, isArchived) => {
                        if (!schedule || !Array.isArray(schedule.weeks)) return;
                        schedule.weeks.forEach(week => {
                            if (!week || !Array.isArray(week.games)) return;
                            week.games.forEach(match => {
                                const res = match?.results;
                                if (!res || (!Array.isArray(res.gameScores) && !res.matchStats)) return;
                                const isIdHome = match.homeTeamId === myTeamId;
                                const isIdAway = match.awayTeamId === myTeamId;
                                const homeTeamName = teamsData.find(t => t.id === match.homeTeamId)?.name || '';
                                const awayTeamName = teamsData.find(t => t.id === match.awayTeamId)?.name || '';
                                const isNameHome = currentTeamName && homeTeamName === currentTeamName;
                                const isNameAway = currentTeamName && awayTeamName === currentTeamName;
                                const teamOnHome = isIdHome || isNameHome;
                                const teamOnAway = isIdAway || isNameAway;
                                if (!teamOnHome && !teamOnAway) return;

                                let homeMatchGamePoints = 0;
                                let awayMatchGamePoints = 0;
                                if (Array.isArray(res.gameScores)) {
                                    res.gameScores.forEach((score) => {
                                        if (score.home > score.away) homeMatchGamePoints++;
                                        else if (score.away > score.home) awayMatchGamePoints++;
                                    });
                                } else {
                                    homeMatchGamePoints = res.matchStats?.home?.gamePoints ?? 0;
                                    awayMatchGamePoints = res.matchStats?.away?.gamePoints ?? 0;
                                }

                                if (teamOnHome) {
                                    if (homeMatchGamePoints > awayMatchGamePoints) teamHistoryAgg.totalMatchWins++;
                                    else if (awayMatchGamePoints > homeMatchGamePoints) teamHistoryAgg.totalMatchLosses++;
                                } else {
                                    if (awayMatchGamePoints > homeMatchGamePoints) teamHistoryAgg.totalMatchWins++;
                                    else if (homeMatchGamePoints > awayMatchGamePoints) teamHistoryAgg.totalMatchLosses++;
                                }

                                const teamMatchPlayerIds = new Set();
                                if (Array.isArray(res.gameScores)) {
                                    res.gameScores.forEach((score, idx) => {
                                        const gameType = gameStructure[idx]?.type;
                                        if (!gameType) return;
                                        if (teamOnHome) {
                                            if (score.home > score.away) teamHistoryAgg.gameWinsCount[gameType]++;
                                            teamHistoryAgg.gameTotals[gameType]++;
                                        } else if (teamOnAway) {
                                            if (score.away > score.home) teamHistoryAgg.gameWinsCount[gameType]++;
                                            teamHistoryAgg.gameTotals[gameType]++;
                                        }

                                        const isSingles = gameType.startsWith('S');
                                        const homePlayers = isSingles ? [res.playerAssignments?.[`${idx}-home-p1`]] : [res.playerAssignments?.[`${idx}-home-p1`], res.playerAssignments?.[`${idx}-home-p2`]];
                                        const awayPlayers = isSingles ? [res.playerAssignments?.[`${idx}-away-p1`]] : [res.playerAssignments?.[`${idx}-away-p1`], res.playerAssignments?.[`${idx}-away-p2`]];
                                        const teamSidePlayers = teamOnHome ? homePlayers : teamOnAway ? awayPlayers : [];
                                        teamSidePlayers.filter(Boolean).forEach(pid => teamMatchPlayerIds.add(pid));
                                        if (isArchived) teamSidePlayers.filter(Boolean).forEach(pid => teamHistoryAgg.formerPlayerIds.add(pid));
                                    });
                                }

                                const hlAny = res.highlights || res.matchStats?.highlights || null;
                                if (Array.isArray(hlAny)) {
                                    hlAny.filter(Boolean).forEach(h => {
                                        const pid = h.playerId || h.player || h.uid || null;
                                        if (pid && !teamMatchPlayerIds.has(pid)) return;
                                        const t = (h.type || h.category || '').toLowerCase();
                                        const vStr = (h.value ?? h.score ?? h.points ?? '').toString();
                                        const v = parseInt(vStr.replace(/[^0-9]/g, ''), 10) || 0;
                                        if (t.includes('180')) teamHistoryAgg.highlights.oneEighties++;
                                        if (t.includes('high out') || t.includes('checkout')) teamHistoryAgg.highlights.highOut = Math.max(teamHistoryAgg.highlights.highOut, v);
                                        if (t.includes('high on') || t.includes('high in') || t.includes('highest in')) teamHistoryAgg.highlights.highOn = Math.max(teamHistoryAgg.highlights.highOn, v);
                                    });
                                } else if (hlAny && typeof hlAny === 'object') {
                                    Object.entries(hlAny).forEach(([pid, h]) => {
                                        if (!teamMatchPlayerIds.has(pid)) return;
                                        if (!h || typeof h !== 'object') return;
                                        const tonsRaw = h.tons || '';
                                        const tonCount = Array.isArray(tonsRaw) ? tonsRaw.length : (typeof tonsRaw === 'string' ? (tonsRaw ? tonsRaw.split(',').map(s => s.trim()).filter(Boolean).length : 0) : Number(tonsRaw || 0));
                                        if (!isNaN(tonCount)) teamHistoryAgg.highlights.tonPlus += tonCount;
                                        const n180 = parseInt(h['180s'] || 0, 10);
                                        if (!isNaN(n180)) teamHistoryAgg.highlights.oneEighties += n180;
                                        const outRaw = h.highOut || h.highestOut || '';
                                        let outMax = 0;
                                        if (Array.isArray(outRaw)) {
                                            outMax = outRaw.reduce((mx, val) => Math.max(mx, parseInt(val, 10) || 0), 0);
                                        } else if (typeof outRaw === 'string') {
                                            const vals = outRaw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
                                            if (vals.length > 0) outMax = Math.max(...vals);
                                        } else {
                                            outMax = Number(outRaw || 0);
                                        }
                                        if (!isNaN(outMax)) teamHistoryAgg.highlights.highOut = Math.max(teamHistoryAgg.highlights.highOut, outMax);
                                        const inRaw = h.highIn || h.on80 || '';
                                        let inMax = 0;
                                        if (Array.isArray(inRaw)) {
                                            inMax = inRaw.reduce((mx, val) => Math.max(mx, parseInt(val, 10) || 0), 0);
                                        } else if (typeof inRaw === 'string') {
                                            const vals = inRaw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
                                            if (vals.length > 0) inMax = Math.max(...vals);
                                        } else {
                                            inMax = Number(inRaw || 0);
                                        }
                                        if (!isNaN(inMax)) teamHistoryAgg.highlights.highOn = Math.max(teamHistoryAgg.highlights.highOn, inMax);
                                    });
                                }
                            });
                        });
                    };

                    // Accumulate archived first (primary), then include current seasons to complete history
                    schedules.filter(s => s.archived).forEach(s => accumulateForSchedule(s, true));
                    schedules.filter(s => !s.archived).forEach(s => accumulateForSchedule(s, false));

                    // Merge Team History after current-season computations using locally fetched roster
                    const currentPlayersList = rosterData.map(p => ({ id: p.id, name: p.nickname || `${p.firstName} ${p.lastName}`, email: (p.email || '').toLowerCase() }));
                    const currentRosterIds = new Set(teamData?.members || []);
                    const formerOnlyIds = Array.from(teamHistoryAgg.formerPlayerIds).filter(pid => !currentRosterIds.has(pid));
                    const formerDocs = await Promise.all(formerOnlyIds.map(uid => getDoc(doc(db, "users", uid))));
                    const formerPlayers = formerDocs
                        .filter(d => d.exists())
                        .map(d => ({ id: d.id, name: d.data().nickname || `${d.data().firstName} ${d.data().lastName}`, email: (d.data().email || '').toLowerCase() }));

                    const normalize = (s) => (s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
                    const currentNameSet = new Set(currentPlayersList.flatMap(p => [normalize(p.name), normalize(p.email)]));
                    const formerPlayersFiltered = formerPlayers.filter(fp => !currentNameSet.has(normalize(fp.name)) && (!fp.email || !currentNameSet.has(normalize(fp.email))));

                    const baseStats = finalTeamStats || {
                        name: teamData?.name || '',
                        gamePoints: 0,
                        matchWins: 0,
                        matchLosses: 0,
                        weeksPlayed: 0,
                        S301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 }, D301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 },
                        S501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 }, D501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 },
                        SCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 }, DCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0 },
                    };

                    if (isMounted) setTeamStats({
                        ...baseStats,
                        history: {
                            totalMatchWins: teamHistoryAgg.totalMatchWins,
                            totalMatchLosses: teamHistoryAgg.totalMatchLosses,
                            gameWinsCount: teamHistoryAgg.gameWinsCount,
                            gameTotals: teamHistoryAgg.gameTotals,
                            highlights: teamHistoryAgg.highlights,
                            currentPlayers: currentPlayersList,
                            formerPlayers: formerPlayersFiltered,
                        }
                    });
                }
            } catch (error) {
                logError("Error fetching team data:", error);
            }
            if (isMounted) setLoading(false);
        };

        fetchTeamData();
        return () => { isMounted = false; };
    }, [teamId]);

    if (loading) return <div className="text-center">Loading team data...</div>;
    if (!teamDetails) return <p className="text-center text-danger">Team not found.</p>;

    const { nextMatch, lastMatch } = matchContext;
    const getOrdinal = (n) => {
        if (!n) return '';
        const s = ["th", "st", "nd", "rd"], v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
    };

    const handleBack = () => {
        const from = pageProps?.from;
        if (from) { navigate(from); return; }
        if (window.history && window.history.length > 1) { window.history.back(); return; }
        navigate('League Teams');
    };

    const handleOpenPreview = (opponentId, isHome) => {
        const currentSchedule = matchContext.nextMatch ? allSchedules.find(s => s.id === matchContext.nextMatch.scheduleId) : null;
        if (!currentSchedule) return;

        const myTeamId = teamId;

        const getMostLikelyPlayers = (tid, schedule) => {
            const slots = Array(9).fill(null).map(() => ({}));
            schedule.weeks.forEach(week => {
                if (!week.games) return;
                week.games.forEach(game => {
                    if (!game.results) return;
                    const isHomeGame = game.homeTeamId === tid;
                    const isAwayGame = game.awayTeamId === tid;
                    if (!isHomeGame && !isAwayGame) return;

                    const prefix = isHomeGame ? 'home' : 'away';
                    for (let i = 0; i < 9; i++) {
                        const p1 = game.results.playerAssignments?.[`${i}-${prefix}-p1`];
                        const p2 = game.results.playerAssignments?.[`${i}-${prefix}-p2`];
                        if (p1) slots[i][p1] = (slots[i][p1] || 0) + 1;
                        if (p2) slots[i][p2] = (slots[i][p2] || 0) + 1;
                    }
                });
            });

            return slots.map((freqMap, idx) => {
                const sorted = Object.entries(freqMap).sort((a, b) => b[1] - a[1]);
                const isSingles = gameStructure[idx].type.startsWith('S');
                return isSingles ? (sorted.length > 0 ? [sorted[0][0]] : []) : (sorted.length > 1 ? [sorted[0][0], sorted[1][0]] : (sorted.length > 0 ? [sorted[0][0]] : []));
            });
        };

        const myLikely = getMostLikelyPlayers(myTeamId, currentSchedule);
        const oppLikely = getMostLikelyPlayers(opponentId, currentSchedule);
        setPreviewData({ opponentName: allTeams.find(t => t.id === opponentId)?.name || 'Unknown', myTeamName: teamDetails.name, isHome, myLikely, oppLikely });
        setPreviewModalOpen(true);
    };

    return (
        <div className="space-y-6">
            <div className="flex">
                <button onClick={handleBack} className="btn btn-primary inline-flex items-center text-sm">
                    &larr; Back
                </button>
            </div>
            <h2 className="page-title heading-primary-1"><span className="subheading-primary-1 inline-block align-bottom whitespace-normal break-words">{teamDetails.name}</span></h2>

            {/* Summary Header */}
            <div className="card p-3 sm:p-5 -mx-2 sm:mx-0">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                    <div className="text-center">
                        <p className="text-xs text-muted">Current Position</p>
                        <p className="text-lg font-bold">{teamRank ? <span className="tag tag-primary-one">{teamRank}{getOrdinal(teamRank)}</span> : <span className="tag tag-neutral">N/A</span>}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-xs text-muted">Home Venue</p>
                        <p className="text-sm text-info">{homeVenue?.name || <span className="tag tag-neutral">N/A</span>}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-xs text-muted">Game Points</p>
                        <p className="text-lg font-bold subheading-primary-1">{teamStats?.gamePoints ?? 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-xs text-muted">Match W-L</p>
                        <p className="text-lg font-bold text-strong">{teamStats ? `${teamStats.matchWins}-${teamStats.matchLosses}` : '0-0'}</p>
                    </div>
                </div>
            </div>

            {/* Roster & Statistics */}
            <div className="card p-3 sm:p-5 -mx-2 sm:mx-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 border-b border-low pb-2">
                    <h3 className="text-xl font-bold heading-primary-1">Roster & Statistics</h3>
                </div>
                
                {/* Roster badges (Compact at top) */}
                <div className="mb-5">
                    <div className="flex flex-wrap gap-2">
                        {(roster.length ? roster.map(p => ({ id: p.id, name: p.nickname || `${p.firstName} ${p.lastName}` })) : []).map(p => (
                            <span key={p.id || p.name} className="tag tag-secondary-one shadow-sm"><span className="inline-block align-bottom whitespace-normal break-words font-semibold">{p.name}</span></span>
                        ))}
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Overall Team Stats - Mobile Friendly Grid */}
                    <div>
                        <h4 className="text-md font-semibold heading-secondary-2 mb-3">Overall Team Stats</h4>
                        {teamStats ? (
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 surface-low p-3 rounded-lg border border-low">
                                    <h5 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Match & Game Points</h5>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between p-1.5 -mx-1.5 rounded hover:surface-low transition-colors cursor-pointer" onClick={() => setSelectedTeamForHistory({ team: teamStats, filter: 'all', display: 'matches' })}>
                                            <span className="text-muted font-medium">Match (W-L)</span> 
                                            <span className="font-bold text-strong">{teamStats.matchWins}-{teamStats.matchLosses}</span>
                                        </div>
                                        <div className="flex justify-between p-1.5 -mx-1.5 rounded hover:surface-low transition-colors cursor-pointer" onClick={() => setSelectedTeamForHistory({ team: teamStats, filter: 'all', display: 'games' })}>
                                            <span className="text-muted font-medium">Game Points</span> 
                                            <span className="font-bold text-info">{teamStats.gamePoints}</span>
                                        </div>
                                        <div className="flex justify-between p-1.5 -mx-1.5 rounded hover:surface-low transition-colors cursor-default">
                                            <span className="text-muted font-medium">Weeks Played</span> 
                                            <span className="font-bold text-strong">{teamStats.weeksPlayed}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-[2] surface-low p-3 rounded-lg border border-low">
                                    <h5 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Record (Leg Win%)</h5>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                                        {['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'].map(type => {
                                            const stats = teamStats[type] || { w: 0, l: 0, winPct: 0 };
                                            const rank = teamStats.ranks?.[type];
                                            return (
                                                <div key={type} className="flex flex-col p-1.5 rounded hover:surface-low transition-colors cursor-pointer" onClick={() => setSelectedTeamForHistory({ team: teamStats, filter: type, display: 'games' })}>
                                                    <div className="flex items-center justify-between mb-0.5">
                                                        <span className="font-medium text-muted">{type}</span>
                                                        {rank > 0 && <span className="tag tag-primary-one text-[10px] px-1 py-0 shadow-sm shrink-0">{rank}{getOrdinal(rank)}</span>}
                                                    </div>
                                                    <span className="font-bold text-strong">{stats.w}-{stats.l} <span className="text-muted font-normal text-xs ml-1">({stats.winPct?.toFixed(3) || '0.000'})</span></span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : <p className="text-muted">No team stats available yet.</p>}
                    </div>

                    {/* Player Performance - Mobile Friendly Cards */}
                    <div>
                        <h4 className="text-md font-semibold heading-primary-2 mb-3">Player Performance</h4>
                    {rosterStats.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {rosterStats.map(player => (
                                    <div key={player.id} className="surface-low border border-low rounded-lg p-3 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-center mb-3 border-b border-low pb-2">
                                            <div className="flex items-center gap-2 overflow-hidden pr-2">
                                                {player.overallRank > 0 && <span className="tag tag-primary-one shrink-0 shadow-sm" title="Overall Rank">{player.overallRank}{getOrdinal(player.overallRank)}</span>}
                                                <span className="font-bold text-strong truncate" title={player.name}>{player.name}</span>
                                            </div>
                                            <span className="tag tag-primary-one text-[10px] sm:text-xs font-bold shrink-0">WP: {player.weeksPlayed}</span>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex justify-between text-sm items-center p-1.5 -mx-1.5 rounded hover:surface-low transition-colors cursor-pointer" onClick={() => setSelectedPlayerForHistory({ player, filter: 'all', display: 'games' })}>
                                                <span className="text-muted font-medium">Games (W-L)</span>
                                                <span className="font-semibold text-strong">{player.totalGamesWon}-{player.totalGamesPlayed - player.totalGamesWon}</span>
                                            </div>
                                            <div className="flex justify-between text-sm items-center p-1.5 -mx-1.5 rounded hover:surface-low transition-colors cursor-pointer" onClick={() => setSelectedPlayerForHistory({ player, filter: 'all', display: 'legs' })}>
                                                <span className="text-muted font-medium">Legs (W-L)</span>
                                                <span className="font-bold subheading-primary-2">{player.totalLegsWon}-{player.totalLegsPlayed - player.totalLegsWon}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                    ) : <p className="text-muted">No player stats available yet.</p>}
                    </div>
                </div>
            </div>

            {/* Upcoming Schedule */}
            <div className="card p-4 sm:p-6">
                <h3 className="heading-primary-1 mb-3">Upcoming Schedule</h3>
                {(() => {
                    // Build upcoming list from currentSchedule-derived context
                    const upcomingList = [];
                    if (nextMatch) upcomingList.push(nextMatch);
                    // We don't have a separate array here; rely on schedule context in matchContext
                    return upcomingList.length > 0 ? (
                        <div className="space-y-3 text-strong">
                            {upcomingList.map((m, idx) => {
                                const isHome = m.homeTeamId === teamId;
                                const opponentId = isHome ? m.awayTeamId : m.homeTeamId;
                                const opponentName = opponentId === 'TBD'
                                    ? (() => { const adv = isHome ? m.advancement?.away : m.advancement?.home; const txt = adv?.from ? `Winner of ${adv.from}` : 'TBD'; return txt; })()
                                    : (allTeams.find(t => t.id === opponentId)?.name || 'TBD');
                                const rank = teamRanksMap[opponentId];
                                const getOrd = (n) => { if (!n) return ''; const s = ["th","st","nd","rd"], v = n % 100; return s[(v-20)%10] || s[v] || s[0]; };
                                return (
                                    <div key={`${m.scheduleId}-${m.weekIndex}-${m.gameIndex}-${idx}`} className="pb-2 last:border-none">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <strong className="font-semibold text-strong whitespace-normal break-words">{opponentName}</strong>
                                                {opponentId === 'TBD' ? <span className="tag tag-neutral">TBD</span> : (rank ? <span className="tag tag-primary-one">{rank}{getOrd(rank)}</span> : null)}
                                                <span className={`tag tag-outline ${isHome ? 'tag-home' : 'tag-away'}`}>{isHome ? 'Home' : 'Away'}</span>
                                            </div>
                                            <span className="text-sm text-info shrink-0">{m.date || 'TBD'}</span>
                                        </div>
                                        <p className="text-sm text-info mt-1">{m.venue || 'TBD'}</p>
                                        {opponentId !== 'TBD' && (
                                            <div className="mt-2">
                                                <button onClick={() => handleOpenPreview(opponentId, isHome)} className="btn btn-secondary text-xs py-1 px-3 rounded-md">Match Preview</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <button onClick={() => navigate('Schedule')} className="btn btn-link mt-1 text-sm">View Full Schedule</button>
                        </div>
                    ) : (
                        <p className="text-muted">No upcoming matches scheduled.</p>
                    );
                })()}
            </div>

            {/* Match History */}
            <div className="card p-4 sm:p-6">
                <h3 className="heading-primary-1 mb-3">Match History</h3>
                {lastMatch ? (
                    <div className="space-y-3 text-strong">
                        {/* Only showing last match for this page; full history available on schedule/results pages */}
                        {(() => {
                            const m = lastMatch;
                            const res = m?.results;
                            let homeScore = null, awayScore = null;
                            if (res && res.matchStats) {
                                homeScore = res.matchStats.home?.gamePoints ?? null;
                                awayScore = res.matchStats.away?.gamePoints ?? null;
                            } else if (typeof m?.result === 'string') {
                                const parts = m.result.split('-');
                                homeScore = parseInt(parts[0]?.trim(), 10);
                                awayScore = parseInt(parts[1]?.trim(), 10);
                            }
                            const isHome = m.homeTeamId === teamId;
                            const scoreText = (homeScore != null && awayScore != null) ? (isHome ? `${homeScore}-${awayScore}` : `${awayScore}-${homeScore}`) : '—';
                            const side = isHome ? 'Home' : 'Away';
                            const didWin = (homeScore != null && awayScore != null) ? (isHome ? (homeScore > awayScore) : (awayScore > homeScore)) : null;
                            const tagTone = didWin == null ? 'tag-neutral' : (didWin ? 'tag-success' : (homeScore === awayScore ? 'tag-neutral' : 'tag-danger'));
                            return (
                                <div key={`${m.scheduleId}-${m.weekIndex}-${m.gameIndex}`} className="pb-2 last:border-none">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm text-strong">{m.date || 'TBD'}</p>
                                        <span className={`tag ${tagTone}`}>{`${side} • ${scoreText}`}</span>
                                    </div>
                                    <p className="text-sm text-strong mt-1">
                                        <span className="text-strong mr-1">vs</span>
                                        <strong className="font-semibold text-strong">
                                            {(() => {
                                                const opponentId = isHome ? m.awayTeamId : m.homeTeamId;
                                                if (opponentId === 'TBD') {
                                                    const advancement = isHome ? m.advancement?.away : m.advancement?.home;
                                                    const txt = advancement?.from ? `Winner of ${advancement.from}` : 'TBD';
                                                    return <span className="tag tag-solid tag-neutral">{txt}</span>;
                                                }
                                                const name = allTeams.find(t => t.id === opponentId)?.name;
                                                return name ? <span className="text-strong">{name}</span> : <span className="tag tag-solid tag-neutral">TBD</span>;
                                            })()}
                                        </strong>
                                        <span> — <span className="text-strong">Venue</span> <span className="text-strong">{m.venue || 'TBD'}</span></span>
                                    </p>
                                    <button onClick={() => navigate('Match Results Summary', { scheduleId: m.scheduleId, weekIndex: m.weekIndex, gameIndex: m.gameIndex, from: 'Team Details', teamId })} className="btn btn-primary mt-2 w-full sm:w-auto text-sm">View Results</button>
                                </div>
                            );
                        })()}
                        <button onClick={() => navigate('Schedule')} className="btn btn-link mt-1 text-sm">View Full History</button>
                    </div>
                ) : (
                    <p className="text-muted">No completed matches yet.</p>
                )}
            </div>

            {/* Team History Section (Cumulative Across All Seasons) */}
            <div className="card p-3 sm:p-5 -mx-2 sm:mx-0">
                <h3 className="text-xl font-bold heading-primary-1 mb-3">Team History</h3>
                {teamStats?.history ? (
                    <div className="space-y-3 sm:space-y-5">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <div className="card text-center rounded-lg p-2 sm:p-3">
                                <p className="text-sm text-muted">Total Match Wins</p>
                                <p className="text-xl sm:text-2xl font-bold subheading-primary-1">{teamStats.history.totalMatchWins}</p>
                            </div>
                            <div className="card text-center rounded-lg p-2 sm:p-3">
                                <p className="text-sm text-muted">Total Match Losses</p>
                                <p className="text-xl sm:text-2xl font-bold subheading-primary-1">{teamStats.history.totalMatchLosses}</p>
                            </div>
                        </div>

                        {(() => {
                            const gwc = teamStats.history.gameWinsCount || {};
                            const gt = teamStats.history.gameTotals || {};
                            const pct = (wins, total) => (total > 0 ? wins / total : 0);
                            const formatPct = (p) => isFinite(p) ? `${Math.round(p * 100)}%` : '—';
                            const tone = (p) => p >= 0.6 ? 'text-success' : p >= 0.4 ? 'text-warning' : 'text-danger';
                            const roleOf = (p) => (p >= 0.6 ? 'success' : (p >= 0.4 ? 'warning' : 'danger'));
                            const singleTypes = ['S301','S501','SCricket'];
                            const doubleTypes = ['D301','D501','DCricket'];
                            const sumWins = (types) => types.reduce((acc, t) => acc + (gwc[t] || 0), 0);
                            const sumTotals = (types) => types.reduce((acc, t) => acc + (gt[t] || 0), 0);
                            const singlesTotals = sumTotals(singleTypes);
                            const doublesTotals = sumTotals(doubleTypes);
                            const singlesWins = sumWins(singleTypes);
                            const doublesWins = sumWins(doubleTypes);
                            const singlesPct = pct(singlesWins, singlesTotals);
                            const doublesPct = pct(doublesWins, doublesTotals);
                            const overallTotals = singlesTotals + doublesTotals;
                            const overallWins = singlesWins + doublesWins;
                            const overallPct = pct(overallWins, overallTotals);
                            const labelMap = { S301: '301 Singles', D301: '301 Doubles', S501: '501 Singles', D501: '501 Doubles', SCricket: 'Cricket Singles', DCricket: 'Cricket Doubles' };
                            const types = Object.keys(labelMap);
                            const bestType = types.reduce((best, k) => {
                                const bp = pct(gwc[best] || 0, gt[best] || 0);
                                const kp = pct(gwc[k] || 0, gt[k] || 0);
                                return kp > bp ? k : best;
                            }, types[0]);
                            const bestPct = pct(gwc[bestType] || 0, gt[bestType] || 0);
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
                                    <div className={`card text-center rounded-lg p-2 sm:p-3 bg-${roleOf(overallPct)}-soft border-l-4 border-${roleOf(overallPct)}`}>
                                        <p className="text-sm text-muted">Overall Win%</p>
                                        <p className={`text-xl sm:text-2xl font-bold ${tone(overallPct)}`}>{formatPct(overallPct)}</p>
                                    </div>
                                    <div className={`card text-center rounded-lg p-2 sm:p-3 bg-${roleOf(singlesPct)}-soft border-l-4 border-${roleOf(singlesPct)}`}>
                                        <p className="text-sm text-muted">Singles Win%</p>
                                        <p className={`text-xl sm:text-2xl font-bold ${tone(singlesPct)}`}>{formatPct(singlesPct)}</p>
                                    </div>
                                    <div className={`card text-center rounded-lg p-2 sm:p-3 bg-${roleOf(doublesPct)}-soft border-l-4 border-${roleOf(doublesPct)}`}>
                                        <p className="text-sm text-muted">Doubles Win%</p>
                                        <p className={`text-xl sm:text-2xl font-bold ${tone(doublesPct)}`}>{formatPct(doublesPct)}</p>
                                    </div>
                                    <div className="md:col-span-3 text-center card rounded-lg p-2 sm:p-3 bg-info-soft border-l-4 border-info">
                                        <p className="text-sm text-muted">Best Game Type</p>
                                        <p className="text-base sm:text-lg font-bold text-info">{labelMap[bestType]} — {formatPct(bestPct)}</p>
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                            {(() => {
                                const gwc = teamStats.history.gameWinsCount || {};
                                const gt = teamStats.history.gameTotals || {};
                                const pct = (wins, total) => (total > 0 ? wins / total : 0);
                                const formatPct = (p) => isFinite(p) ? `${Math.round(p * 100)}%` : '—';
                                const roleOf = (p) => (p >= 0.6 ? 'success' : (p >= 0.4 ? 'warning' : 'danger'));
                                const tone = (p) => p >= 0.6 ? 'text-success' : p >= 0.4 ? 'text-warning' : 'text-danger';
                                const cards = [
                                    { key: 'S301', label: '301 Singles' },
                                    { key: 'D301', label: '301 Doubles' },
                                    { key: 'S501', label: '501 Singles' },
                                    { key: 'D501', label: '501 Doubles' },
                                    { key: 'SCricket', label: 'Cricket Singles' },
                                    { key: 'DCricket', label: 'Cricket Doubles' },
                                ];
                                return cards.map(c => {
                                    const p = pct(gwc[c.key] || 0, gt[c.key] || 0);
                                    const roleCls = roleOf(p);
                                    return (
                                        <div key={c.key} className={`card text-center rounded-lg p-2 sm:p-3 bg-${roleCls}-soft border-l-4 border-${roleCls}`}>
                                            <p className="text-xs text-muted">{c.label} Win%</p>
                                            <p className={`text-base sm:text-lg font-bold ${tone(p)}`}>{formatPct(p)}</p>
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {(() => {
                            const hl = teamStats.history.highlights || { tonPlus: 0, oneEighties: 0, highOut: 0, highOn: 0 };
                            return (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                                    <div className="card text-center rounded-lg p-2 sm:p-3">
                                        <p className="text-xs text-muted">Ton+</p>
                                        <p className="text-lg sm:text-xl font-bold subheading-primary-1">{hl.tonPlus}</p>
                                    </div>
                                    <div className="card text-center rounded-lg p-2 sm:p-3">
                                        <p className="text-xs text-muted">180s</p>
                                        <p className="text-lg sm:text-xl font-bold subheading-primary-1">{hl.oneEighties}</p>
                                    </div>
                                    <div className="card text-center rounded-lg p-2 sm:p-3">
                                        <p className="text-xs text-muted">High Out</p>
                                        <p className="text-lg sm:text-xl font-bold subheading-primary-1">{hl.highOut || '—'}</p>
                                    </div>
                                    <div className="card text-center rounded-lg p-2 sm:p-3">
                                        <p className="text-xs text-muted">High On</p>
                                        <p className="text-lg sm:text-xl font-bold subheading-primary-1">{hl.highOn || '—'}</p>
                                    </div>
                                </div>
                            );
                        })()}

                        <div>
                            <h4 className="text-lg sm:text-xl font-semibold heading-primary-1 mb-2">Former Players</h4>
                            <div className="flex flex-wrap justify-center gap-2">
                                {(() => {
                                    const list = teamStats?.history?.formerPlayers || [];
                                    const keyFor = (p) => p.id || p.email || (p.name || '').toLowerCase();
                                    const map = new Map();
                                    list.forEach(p => { if (p) map.set(keyFor(p), p); });
                                    const unique = Array.from(map.values());
                                    return unique.length > 0 ? unique.map(p => (
                                        <span key={p.id || p.email || (p.name || 'unknown')} className="badge"><span className="inline-block max-w-[12rem] truncate align-bottom">{p.name || 'Unknown'}</span></span>
                                    )) : <span className="text-sm text-muted">None</span>;
                                })()}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-muted">Cumulative team history will appear after your first recorded match.</p>
                )}
            </div>

            {previewModalOpen && previewData && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setPreviewModalOpen(false)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">Match Preview</h3>
                            <button onClick={() => setPreviewModalOpen(false)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-4 overflow-y-auto space-y-4">
                            <p className="text-center text-muted text-sm mb-2">
                                Predicted matchups based on this season's data.
                            </p>
                            
                            <div className="hidden sm:block overflow-x-auto rounded-lg border border-low">
                                <table className="min-w-full text-sm">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="py-2 px-3 text-left font-semibold">Game</th>
                                            <th className="py-2 px-3 text-left font-semibold">{previewData.isHome ? previewData.opponentName : previewData.myTeamName}</th>
                                            <th className="py-2 px-3 text-center text-muted font-semibold">vs</th>
                                            <th className="py-2 px-3 text-right font-semibold">{previewData.isHome ? previewData.myTeamName : previewData.opponentName}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gameStructure.map((game, idx) => {
                                            const getPlayerDisplay = (id, gameType) => {
                                                const p = allPlayers.find(u => u.id === id);
                                                const name = p ? (p.nickname || p.firstName) : 'Unknown';
                                                const stats = allPlayersStats[id]?.[gameType];
                                                const record = stats ? `(${stats.w}-${stats.l})` : '(0-0)';
                                                return `${name} ${record}`;
                                            };
                                            const myPlayers = previewData.myLikely[idx].map(id => getPlayerDisplay(id, game.type)).join(' & ') || 'TBD';
                                            const oppPlayers = previewData.oppLikely[idx].map(id => getPlayerDisplay(id, game.type)).join(' & ') || 'TBD';
                                            const homePlayers = previewData.isHome ? myPlayers : oppPlayers;
                                            const awayPlayers = previewData.isHome ? oppPlayers : myPlayers;
                                            return (
                                                <tr key={idx} className="border-b border-low row-alt">
                                                    <td className="py-3 px-3 font-medium text-strong">{game.name}</td>
                                                    <td className="py-3 px-3">{awayPlayers}</td>
                                                    <td className="py-3 px-3 text-center text-muted text-xs font-bold">VS</td>
                                                    <td className="py-3 px-3 text-right">{homePlayers}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="sm:hidden space-y-2">
                                {gameStructure.map((game, idx) => {
                                    const getPlayerDisplay = (id, gameType) => {
                                        const p = allPlayers.find(u => u.id === id);
                                        const name = p ? (p.nickname || p.firstName) : 'Unknown';
                                        const stats = allPlayersStats[id]?.[gameType];
                                        const record = stats ? `(${stats.w}-${stats.l})` : '(0-0)';
                                        return `${name} ${record}`;
                                    };
                                    const myPlayers = previewData.myLikely[idx].length > 0 ? previewData.myLikely[idx].map(id => <div key={id} className="truncate">{getPlayerDisplay(id, game.type)}</div>) : 'TBD';
                                    const oppPlayers = previewData.oppLikely[idx].length > 0 ? previewData.oppLikely[idx].map(id => <div key={id} className="truncate">{getPlayerDisplay(id, game.type)}</div>) : 'TBD';
                                    const homePlayers = previewData.isHome ? myPlayers : oppPlayers;
                                    const awayPlayers = previewData.isHome ? oppPlayers : myPlayers;
                                    return (
                                        <div key={idx} className="surface-low p-3 rounded-lg border border-low">
                                            <div className="text-xs font-bold text-muted uppercase tracking-wider mb-2 text-center">{game.name}</div>
                                            <div className="flex justify-between items-center gap-2">
                                                <div className="flex-1 min-w-0 text-sm font-medium text-left">{awayPlayers}</div>
                                                <div className="text-[10px] text-muted font-bold px-1 shrink-0">VS</div>
                                                <div className="flex-1 min-w-0 text-sm font-medium text-right">{homePlayers}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTeamForHistory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setSelectedTeamForHistory(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedTeamForHistory.team.name}
                            </h3>
                            <button onClick={() => setSelectedTeamForHistory(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {(() => {
                                const { team, filter, display } = selectedTeamForHistory;
                                const historyList = display === 'matches' 
                                    ? team.matchHistory 
                                    : team.gameHistory.filter(g => filter === 'all' || g.gameType === filter);

                                if (historyList.length === 0) {
                                    return <p className="text-center text-muted py-4">No match history available.</p>;
                                }
                                return historyList.map((item, i) => {
                                    const opponent = allTeams.find(t => t.id === item.opponentId);
                                    const oppName = opponent ? opponent.name : 'Unknown';
                                    
                                    let highlightClass = '';
                                    if (item.teamRecordAtTime && item.opponentRecordAtTime) {
                                        let compPct = item.opponentRecordAtTime.winPct;
                                        if (item.won && item.teamRecordAtTime.winPct < compPct) {
                                            highlightClass = 'bg-success-soft';
                                        } else if (!item.won && item.teamRecordAtTime.winPct > compPct) {
                                            highlightClass = 'bg-danger-soft';
                                        }
                                    }

                                    const gameTitle = display === 'matches' 
                                        ? 'Match' 
                                        : (filter !== 'all' ? item.gameName.replace(' Singles', '').replace(' Doubles', '') : item.gameName);

                                    let teamPlayersStr = '';
                                    let oppPlayersStr = '';
                                    if (item.isSingles) {
                                        const getPName = ids => ids?.map(id => allPlayers.find(p => p.id === id)?.nickname || allPlayers.find(p => p.id === id)?.firstName || 'Unknown').join(' & ');
                                        teamPlayersStr = getPName(item.teamPlayerIds);
                                        oppPlayersStr = getPName(item.opponentPlayerIds);
                                    }

                                    return (
                                        <div key={i} className={`p-3 rounded-lg border border-low flex flex-col sm:flex-row justify-between gap-3 ${highlightClass || 'surface-low'}`}>
                                            <div className="flex flex-col justify-center">
                                                <span className="font-bold text-sm text-strong">Week {item.week} - {gameTitle}</span>
                                                <span className="text-xs text-muted">{item.date}</span>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-2/3">
                                                <div className="flex flex-col text-left sm:text-right">
                                                    <span className="text-sm font-semibold text-strong">vs {oppName}</span>
                                                    {item.isSingles && teamPlayersStr && oppPlayersStr && (
                                                        <span className="text-xs font-medium text-info mb-0.5">{teamPlayersStr} vs {oppPlayersStr}</span>
                                                    )}
                                                    <span className="text-xs text-muted">Opp: {item.opponentRecordAtTime?.w || 0}-{item.opponentRecordAtTime?.l || 0}</span>
                                                    <span className="text-xs text-muted">Team: {item.teamRecordAtTime?.w || 0}-{item.teamRecordAtTime?.l || 0}</span>
                                                </div>
                                                <div className="text-center shrink-0">
                                                    <span className={`text-2xl font-extrabold ${item.won ? 'text-success' : 'text-danger'}`}>
                                                        {item.won ? 'W' : 'L'}
                                                    </span>
                                                    <span className="text-sm font-bold ml-1 text-strong block sm:inline">
                                                        {display === 'matches' ? `(${item.teamPoints}-${item.opponentPoints})` : `(${item.teamLegs}-${item.opponentLegs})`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="p-3 sm:p-4 border-t border-low surface-low rounded-b-lg">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-[10px] sm:text-xs text-muted justify-center sm:justify-start">
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-success-soft mr-1.5 rounded-sm"></span> Won vs better record
                                </div>
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-danger-soft mr-1.5 rounded-sm"></span> Lost vs worse record
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedPlayerForHistory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setSelectedPlayerForHistory(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedPlayerForHistory.player.name}
                            </h3>
                            <button onClick={() => setSelectedPlayerForHistory(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {(() => {
                                const filteredHistory = selectedPlayerForHistory.player.gameHistory.filter(game => {
                                    const filterType = selectedPlayerForHistory.filter;
                                    if (filterType === 'all') return true;
                                    if (filterType === 'singles') return game.isSingles;
                                    return game.gameType === filterType;
                                });

                                if (filteredHistory.length === 0) {
                                    return <p className="text-center text-muted py-4">No match history available.</p>;
                                }
                                return filteredHistory.map((game, i) => {
                                    const opponents = game.opponentIds.map(id => allPlayersStats[id]).filter(Boolean);
                                    const oppNames = opponents.map(o => o.name).join(' & ');
                                    
                                    let playerRecordDisplay = 'N/A';
                                    let oppRecordDisplay = 'N/A';
                                    let highlightClass = '';

                                    if (game.playerRecordAtTime) {
                                        playerRecordDisplay = `${game.playerRecordAtTime.w}-${game.playerRecordAtTime.l}`;
                                    }

                                    if (game.opponentRecordsAtTime && game.opponentRecordsAtTime.length > 0) {
                                        const opp1Record = game.opponentRecordsAtTime[0];
                                        oppRecordDisplay = `${opp1Record.w}-${opp1Record.l}`;
                                        
                                        let compPct = opp1Record.winPct;
                                        
                                        if (game.opponentRecordsAtTime.length > 1 && game.opponentRecordsAtTime[1]) {
                                            const opp2Record = game.opponentRecordsAtTime[1];
                                            if (opp2Record) {
                                                oppRecordDisplay = `${opp1Record.w}-${opp1Record.l} & ${opp2Record.w}-${opp2Record.l}`;
                                                compPct = (opp1Record.winPct + opp2Record.winPct) / 2;
                                            }
                                        }

                                        if (game.playerRecordAtTime) {
                                            if (game.won && game.playerRecordAtTime.winPct < compPct) {
                                                highlightClass = 'bg-success-soft';
                                            } else if (!game.won && game.playerRecordAtTime.winPct > compPct) {
                                                highlightClass = 'bg-danger-soft';
                                            }
                                        }
                                    }

                                    return (
                                        <div key={i} className={`p-3 rounded-lg border border-low flex flex-col sm:flex-row justify-between gap-3 ${highlightClass || 'surface-low'}`}>
                                            <div className="flex flex-col justify-center">
                                                <span className="font-bold text-sm text-strong">Week {game.week} - {selectedPlayerForHistory.filter !== 'all' ? game.gameName.replace(' Singles', '') : game.gameName}</span>
                                                <span className="text-xs text-muted">{game.date}</span>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-2/3">
                                                <div className="flex flex-col text-left sm:text-right">
                                                    <span className="text-sm font-semibold text-strong">vs {oppNames || 'Unknown'}</span>
                                                    <span className="text-xs text-muted">Opp: {oppRecordDisplay}</span>
                                                    <span className="text-xs text-muted">Player: {playerRecordDisplay}</span>
                                                </div>
                                                <div className="text-center shrink-0">
                                                    <span className={`text-2xl font-extrabold ${game.won ? 'text-success' : 'text-danger'}`}>
                                                        {game.won ? 'W' : 'L'}
                                                    </span>
                                                    <span className="text-sm font-bold ml-1 text-strong block sm:inline">
                                                        ({game.playerLegs}-{game.opponentLegs})
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="p-3 sm:p-4 border-t border-low surface-low rounded-b-lg">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-[10px] sm:text-xs text-muted justify-center sm:justify-start">
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-success-soft mr-1.5 rounded-sm"></span> Won vs better record
                                </div>
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-danger-soft mr-1.5 rounded-sm"></span> Lost vs worse record
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};